import { getAccessToken } from '../../../../Services/getAccessToken';
import { ENVIRONMENT_URL, RAJPURA_TABLES, getRajpuraTourId, sanitizeParentTourPayload } from '../../../../constants/dataverse';
import {
    STORES,
    putToStore,
    getAllFromStore,
    getFromStore,
    enqueueSync,
    getMixingBakingCyclesForTour
} from '../../../../Services/offlineDB';
import type { MBSessionData, MBCycleRecord, CycleAttachment } from '../context/MixingAndBakingContext';
import { createDefaultCycleRecord } from '../context/MixingAndBakingContext';

const TABLES = {
    QUALITY_TOUR: RAJPURA_TABLES.QUALITY_TOUR,
    MIXING_BAKING: RAJPURA_TABLES.MIXING_AND_BAKING
};

const CONFIG_CACHE_KEY = "mrs_bectors_mixing_baking_config";

export const mixingAndBakingService = {
    /**
     * Fetch QA / Production Personnel Config from SharePoint or Local Cache
     */
    getConfig: async function (accessToken?: string) {
        if (navigator.onLine && accessToken) {
            const listNames = ["Quality-Rajpura-MixingBaking", "Quality-Rajpura-CCPOPRP", "Quality-Rajpura", "Quality-Rajpura-FoodSafety"];
            const { buildSharePointListItemsUrl, SP_SITE_URL } = await import('../../../../constants/sharepoint');

            for (const listName of listNames) {
                try {
                    const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${listName}')/Fields?$select=InternalName,Title,TypeAsString`;
                    const fieldsResponse = await fetch(fieldsUrl, {
                        headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                    });

                    if (fieldsResponse.ok) {
                        const fieldsData = await fieldsResponse.json();
                        const listFields = fieldsData.d?.results || [];

                        const findField = (possibleNames: string[], displayName: string) => {
                            let match = listFields.find((f: any) => possibleNames.includes(f.InternalName));
                            if (match) return match;
                            return listFields.find((f: any) => f.Title === displayName);
                        };

                        const configTypeField = findField(["ConfigType", "Config_x0020_Type"], "Config Type");
                        const plantField = findField(["Plant"], "Plant");
                        const assignedQAField = findField(["AssignedQA", "Assigned_x0020_QA", "AssignedUser", "Assigned_x0020_User", "QAExecutive", "QA_x0020_Executive"], "QA Executive") || findField(["AssignedQA", "Assigned_x0020_QA"], "Assigned QA");
                        const productionInchargeField = findField(["ProductionIncharge", "Production_x0020_Incharge", "ProductionExecutive", "Production_x0020_Executive"], "Production Executive") || findField(["ProductionIncharge", "Production_x0020_Incharge"], "Production Incharge");

                        const selectParts = ["Id", "Title"];
                        const expandParts: string[] = [];

                        if (plantField) selectParts.push(plantField.InternalName);
                        if (configTypeField) selectParts.push(configTypeField.InternalName);

                        if (assignedQAField) {
                            const name = assignedQAField.InternalName;
                            selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                            expandParts.push(name);
                        }
                        if (productionInchargeField) {
                            const name = productionInchargeField.InternalName;
                            selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                            expandParts.push(name);
                        }

                        let query = `$select=${selectParts.join(",")}`;
                        if (expandParts.length > 0) {
                            query += `&$expand=${expandParts.join(",")}`;
                        }

                        const itemsUrl = buildSharePointListItemsUrl(listName, query);
                        const itemsResponse = await fetch(itemsUrl, {
                            headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                        });

                        if (itemsResponse.ok) {
                            const itemsData = await itemsResponse.json();
                            const results = itemsData.d?.results || [];

                            if (results.length > 0) {
                                const parsed = results.map((item: any) => {
                                    const qaFieldKey = assignedQAField?.InternalName;
                                    const prodFieldKey = productionInchargeField?.InternalName;
                                    const rawQA = qaFieldKey ? item[qaFieldKey] : (item.AssignedQA || item.AssignedUser || item.QAExecutive || item.QA_x0020_Executive);
                                    const rawProd = prodFieldKey ? item[prodFieldKey] : (item.ProductionIncharge || item.Production_x0020_Incharge || item.ProductionExecutive || item.Production_x0020_Executive);

                                    let qaName = "QA Officer";
                                    let prodName = "Production Incharge";

                                    const extractName = (raw: any): string | null => {
                                        if (!raw) return null;
                                        if (typeof raw === 'object') {
                                            if (raw.results && Array.isArray(raw.results)) {
                                                const names = raw.results.map((r: any) => r.Title || r.EMail).filter(Boolean);
                                                if (names.length > 0) return names.join(", ");
                                            }
                                            return raw.Title || raw.EMail || JSON.stringify(raw);
                                        }
                                        return String(raw);
                                    };

                                    const extractedQa = extractName(rawQA);
                                    if (extractedQa) qaName = extractedQa;
                                    
                                    const extractedProd = extractName(rawProd);
                                    if (extractedProd) prodName = extractedProd;

                                    return {
                                        id: item.Id,
                                        title: item.Title,
                                        configType: configTypeField ? item[configTypeField.InternalName] : item.ConfigType,
                                        plant: plantField ? item[plantField.InternalName] : item.Plant,
                                        qaExecutive: qaName,
                                        productionIncharge: prodName
                                    };
                                });

                                try {
                                    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(parsed));
                                    await putToStore(STORES.CONFIG, { id: CONFIG_CACHE_KEY, data: parsed });
                                } catch (_) {}

                                return parsed;
                            }
                        }
                    }
                } catch (e) {
                    console.warn(`Could not read SharePoint config from list '${listName}':`, e);
                }
            }
        }

        // 2. Offline / Cache fallback
        try {
            const cachedFromStore = await getFromStore(STORES.CONFIG, CONFIG_CACHE_KEY);
            if (cachedFromStore && cachedFromStore.data) return cachedFromStore.data;

            const cachedFromLocal = localStorage.getItem(CONFIG_CACHE_KEY);
            if (cachedFromLocal) return JSON.parse(cachedFromLocal);
        } catch (_) {}

        // Fallback default list
        return [
            { id: 1, qaExecutive: "David QA", productionIncharge: "John Production", plant: "Rajpura" },
            { id: 2, qaExecutive: "Emily QA", productionIncharge: "Alice Production", plant: "Rajpura" },
            { id: 3, qaExecutive: "Amit Kumar", productionIncharge: "Rajesh Singh", plant: "Rajpura" }
        ];
    },

    /**
     * Create or Retrieve Tour in Dataverse / Local Store
     */
    createOrGetTour: async function (session: MBSessionData, token?: string): Promise<string> {
        const localId = `tour_mb_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        const dateStr = new Date(session.tourStartDate || new Date()).toLocaleDateString('en-GB').replace(/\//g, '');
        const tourTitle = `MixingBaking_Rajpura_${session.line}_${session.shift}_${dateStr}`;

        const tourPayload = {
            cr3ea_title: tourTitle,
            cr3ea_plantid: session.site || "Rajpura",
            cr3ea_lineno: session.line,
            cr3ea_shift: session.shift,
            cr3ea_tourby: session.qaExecutive,
            cr3ea_assigned_qa: session.qaExecutive,
            cr3ea_shiftexecutiveproduction: session.productionIncharge,
            cr3ea_tourstartdate: session.tourStartDate || new Date().toISOString(),
            cr3ea_status: "In Progress",
            cr3ea_processstatus: "In Progress",
            cr3ea_mixing_baking_productname: session.productName,
            cr3ea_mixing_baking_executivename: session.executiveName,
            cr3ea_mixing_baking_batchno: session.batchNo,
            cr3ea_mixing_baking_typeofmixer: session.typeOfMixer
        };

        // Cache locally in IndexedDB first
        await putToStore(STORES.MIXING_BAKING_SESSIONS, {
            id: localId,
            tourTitle,
            ...tourPayload,
            sessionData: session,
            createdLocally: true,
            timestamp: Date.now()
        });

        if (navigator.onLine) {
            let actualToken = token;
            if (!actualToken) {
                const tokenRes = await getAccessToken();
                actualToken = tokenRes?.token;
            }

            if (actualToken) {
                const apiVersion = "9.2";
                const baseApiUrl = ENVIRONMENT_URL || '';

                const tourTableOptions = [
                    RAJPURA_TABLES.QUALITY_TOUR
                ];

                for (const tTable of tourTableOptions) {
                    try {
                        const currentPayload = sanitizeParentTourPayload(tourPayload);
                        delete currentPayload.cr3ea_prod_rajpura_quality_tourid;
                        delete currentPayload.cr3ea_rajpura_quality_tourid;
                        delete currentPayload.cr3ea_prod_qualitytourid;
                        delete currentPayload.cr3ea_qualitytourid;
                        delete currentPayload.id;

                        const res = await fetch(`${baseApiUrl}/api/data/v${apiVersion}/${tTable}`, {
                            method: "POST",
                            headers: {
                                "Accept": "application/json",
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${actualToken}`,
                                "Prefer": "return=representation"
                            },
                            body: JSON.stringify(currentPayload)
                        });

                        if (res.ok) {
                            const resData = await res.json();
                            const serverId = getRajpuraTourId(resData) || localId;
                            // Update local store with server ID
                            await putToStore(STORES.MIXING_BAKING_SESSIONS, {
                                id: serverId,
                                localId,
                                tourTitle,
                                ...tourPayload,
                                cr3ea_prod_rajpura_quality_tourid: serverId,
                                cr3ea_rajpura_quality_tourid: serverId,
                                sessionData: session,
                                synced: true
                            });
                            return serverId;
                        }
                    } catch (e) {
                        console.warn(`Failed creating tour on Dataverse table '${tTable}':`, e);
                    }
                }
            }
        }

        // Offline or fallback
        await enqueueSync({
            id: `sync_tour_${localId}`,
            type: 'SESSION_CREATE_MIXING_BAKING',
            payload: { ...tourPayload, localId },
            timestamp: Date.now()
        });

        return localId;
    },

    /**
     * Update Tour Status in Dataverse and Local Store
     */
    updateTourStatus: async function (tourId: string, status: string = "Completed", token?: string): Promise<boolean> {
        const cleanTourId = tourId.replace(/[{}]/g, "").trim();
        const payload = {
            cr3ea_status: status,
            cr3ea_processstatus: status
        };

        try {
            const localSession = await getFromStore(STORES.MIXING_BAKING_SESSIONS, cleanTourId);
            if (localSession) {
                await putToStore(STORES.MIXING_BAKING_SESSIONS, {
                    ...localSession,
                    ...payload,
                    updatedAt: Date.now()
                });
            }
        } catch (_) {}

        if (navigator.onLine) {
            let actualToken = token;
            if (!actualToken) {
                const tokenRes = await getAccessToken();
                actualToken = tokenRes?.token;
            }

            if (actualToken && cleanTourId && !cleanTourId.startsWith("tour_mb_")) {
                const apiVersion = "9.2";
                const baseApiUrl = ENVIRONMENT_URL || '';
                const tourTableOptions = [RAJPURA_TABLES.QUALITY_TOUR];

                for (const tTable of tourTableOptions) {
                    try {
                        const res = await fetch(`${baseApiUrl}/api/data/v${apiVersion}/${tTable}(${cleanTourId})`, {
                            method: "PATCH",
                            headers: {
                                "Accept": "application/json",
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${actualToken}`,
                                "Prefer": "return=representation"
                            },
                            body: JSON.stringify(payload)
                        });
                        if (res.ok) {
                            return true;
                        }
                    } catch (e) {
                        console.warn(`Failed updating tour status on table '${tTable}':`, e);
                    }
                }
            }
        }

        await enqueueSync({
            id: `sync_tour_status_${cleanTourId}_${Date.now()}`,
            type: 'SESSION_UPDATE_MIXING_BAKING',
            payload: { tourId: cleanTourId, ...payload },
            timestamp: Date.now()
        });
        return true;
    },

    /**
     * Save a Cycle Record (Dataverse wide row + IndexedDB)
     */
    saveCycleRecord: async function (tourId: string, cycleRecord: MBCycleRecord, token?: string): Promise<{ success: boolean; id: string; message: string }> {
        const cleanTourId = tourId.replace(/[{}]/g, "").trim();
        const cycleId = cycleRecord.id || `mb_cycle_${cleanTourId}_${cycleRecord.cycle}_${Date.now()}`;
        const recordToSave: MBCycleRecord = { ...cycleRecord, id: cycleId, tourId: cleanTourId };

        // 1. Build Dataverse wide-table payload exactly matching column definitions
        const widePayload: Record<string, any> = {
            cr3ea_title: `MixingBaking_${cycleRecord.lineNo}_${cycleRecord.cycle}_${cycleRecord.batchNo || Date.now()}`,
            cr3ea_cycle: cycleRecord.cycle,
            cr3ea_shift: cycleRecord.shift,
            cr3ea_observedby: cycleRecord.observedBy,
            cr3ea_productname: cycleRecord.productName,
            cr3ea_lineno: cycleRecord.lineNo,
            cr3ea_productionincharge: cycleRecord.productionIncharge,
            cr3ea_batchno: cycleRecord.batchNo,

            // Ingredient Temperatures (Standard / Observed / Remarks / Action Taken)
            cr3ea_rpostandard: cycleRecord.rpo.standard,
            cr3ea_rpoobserved: cycleRecord.rpo.observed,
            cr3ea_rporemarks: cycleRecord.rpo.remarks,
            cr3ea_rpoactiontaken: cycleRecord.rpo.actionTaken,

            cr3ea_solidfatstandard: cycleRecord.solidFat.standard,
            cr3ea_solidfatobserved: cycleRecord.solidFat.observed,
            cr3ea_solidfatremarks: cycleRecord.solidFat.remarks,
            cr3ea_solidfatactiontaken: cycleRecord.solidFat.actionTaken,

            cr3ea_butterstandard: cycleRecord.butter.standard,
            cr3ea_butterobserved: cycleRecord.butter.observed,
            cr3ea_butterremarks: cycleRecord.butter.remarks,
            cr3ea_butteractiontaken: cycleRecord.butter.actionTaken,

            cr3ea_blackjackstandard: cycleRecord.blackjackInitial.standard,
            cr3ea_blackjackobserved: cycleRecord.blackjackInitial.observed,
            cr3ea_blackjackremarks: cycleRecord.blackjackInitial.remarks,
            cr3ea_blackjackactiontaken: cycleRecord.blackjackInitial.actionTaken,

            cr3ea_spongetempstandard: cycleRecord.sponge.standard,
            cr3ea_spongetempobserved: cycleRecord.sponge.observed,
            cr3ea_spongetempremarks: cycleRecord.sponge.remarks,
            cr3ea_spongetempactiontaken: cycleRecord.sponge.actionTaken,

            cr3ea_slurrystandard: cycleRecord.slurry.standard,
            cr3ea_slurryobserved: cycleRecord.slurry.observed,
            cr3ea_slurryremarks: cycleRecord.slurry.remarks,
            cr3ea_slurryactiontaken: cycleRecord.slurry.actionTaken,

            cr3ea_groundsugartempstandard: cycleRecord.groundSugarTemp.standard,
            cr3ea_groundsugartempobserved: cycleRecord.groundSugarTemp.observed,
            cr3ea_groundsugartempremarks: cycleRecord.groundSugarTemp.remarks,
            cr3ea_groundsugartempactiontaken: cycleRecord.groundSugarTemp.actionTaken,

            cr3ea_groundsugarparticlesizestandard: cycleRecord.groundSugarParticleSize.standard,
            cr3ea_groundsugarparticlesizeobserved: cycleRecord.groundSugarParticleSize.observed,
            cr3ea_groundsugarparticlesizeremarks: cycleRecord.groundSugarParticleSize.remarks,
            cr3ea_groundsugarparticlesizeactiontaken: cycleRecord.groundSugarParticleSize.actionTaken,

            // Choco Chips, Cashew, Invert Syrup, Blackjack 2, Flour
            cr3ea_chocochipssupplier: cycleRecord.rawMaterials.chocoChipsSupplier,
            cr3ea_chocochipstemp: cycleRecord.rawMaterials.chocoChipsTemp,
            cr3ea_chocochipscountperkg: cycleRecord.rawMaterials.chocoChipsCountPerKg,
            cr3ea_chocochipsmfgdate: cycleRecord.rawMaterials.chocoChipsMfgDate,
            cr3ea_chocochipscompoundorpure: cycleRecord.rawMaterials.chocoChipsCompoundOrPure,

            cr3ea_cashewsupplier: cycleRecord.rawMaterials.cashewSupplier,
            cr3ea_cashewtemp: cycleRecord.rawMaterials.cashewTemp,
            cr3ea_cashewcountperkg: cycleRecord.rawMaterials.cashewCountPerKg,
            cr3ea_cashewmfgdate: cycleRecord.rawMaterials.cashewMfgDate,
            cr3ea_cashewcompoundorpure: cycleRecord.rawMaterials.cashewCompoundOrPure,

            cr3ea_invertsyruptemp: cycleRecord.rawMaterials.invertSyrupTemp,
            cr3ea_invertsyrupph: cycleRecord.rawMaterials.invertSyrupPh,
            cr3ea_invertsyrupbrix: cycleRecord.rawMaterials.invertSyrupBrix,

            cr3ea_blackjack2temp: cycleRecord.rawMaterials.blackjack2Temp,
            cr3ea_blackjack2ph: cycleRecord.rawMaterials.blackjack2Ph,
            cr3ea_blackjack2brix: cycleRecord.rawMaterials.blackjack2Brix,

            cr3ea_floursupplier: cycleRecord.rawMaterials.flourSupplier,

            // Minor Ingredients Checklist (Done/Not Done)
            cr3ea_cocoapowderdone: cycleRecord.minorIngredients.cocoaPowder || "Done",
            cr3ea_smpdone: cycleRecord.minorIngredients.smp || "Done",
            cr3ea_salt1done: cycleRecord.minorIngredients.salt1 || "Done",
            cr3ea_abcdone: cycleRecord.minorIngredients.abc || "Done",
            cr3ea_sbcdone: cycleRecord.minorIngredients.sbc || "Done",
            cr3ea_salt2done: cycleRecord.minorIngredients.salt2 || "Done",
            cr3ea_othersdone: cycleRecord.minorIngredients.others || "Done",

            // Mixing Dough (Standard / Observed)
            cr3ea_creamingtimestandard: cycleRecord.mixingDough.creamingTimeStd,
            cr3ea_creamingtimeobserved: cycleRecord.mixingDough.creamingTimeObs,
            cr3ea_mixingtimestandard: cycleRecord.mixingDough.mixingTimeStd,
            cr3ea_mixingtimeobserved: cycleRecord.mixingDough.mixingTimeObs,
            cr3ea_doughtempstandard: cycleRecord.mixingDough.doughTempStd,
            cr3ea_doughtempobserved: cycleRecord.mixingDough.doughTempObs,
            cr3ea_jackettempstandard: cycleRecord.mixingDough.jacketTempStd,
            cr3ea_jackettempobserved: cycleRecord.mixingDough.jacketTempObs,
            cr3ea_doughconsistencystandard: cycleRecord.mixingDough.doughConsistencyStd,
            cr3ea_doughconsistencyobserved: cycleRecord.mixingDough.doughConsistencyObs,
            cr3ea_doughstandingtimestandard: cycleRecord.mixingDough.doughStandingTimeStd,
            cr3ea_doughstandingtimeobserved: cycleRecord.mixingDough.doughStandingTimeObs,

            // Mixing Sponge (single value each)
            cr3ea_spongeproductname: cycleRecord.mixingSponge.spongeProductName,
            cr3ea_spongewaterquantity: cycleRecord.mixingSponge.spongeWaterQuantity,
            cr3ea_spongeyeastquantity: cycleRecord.mixingSponge.spongeYeastQuantity,
            cr3ea_spongewatertemp: cycleRecord.mixingSponge.spongeWaterTemp,
            cr3ea_spongemixingtime: cycleRecord.mixingSponge.spongeMixingTime,
            cr3ea_fermentationstarttemp: cycleRecord.mixingSponge.fermentationStartTemp,
            cr3ea_fermentationroomtemp: cycleRecord.mixingSponge.fermentationRoomTemp,
            cr3ea_finaltempafterfermentation: cycleRecord.mixingSponge.finalTempAfterFermentation,
            cr3ea_finalphafterfermentation: cycleRecord.mixingSponge.finalPhAfterFermentation,

            // Type of Mixer, Forming
            cr3ea_typeofmixer: cycleRecord.forming.typeofMixer,
            cr3ea_moulderrpmstrokes: cycleRecord.forming.moulderRpmStrokes,
            cr3ea_formingsamplecount: cycleRecord.forming.formingSampleCount,
            cr3ea_standardwetweight: cycleRecord.forming.standardWetWeight,
            cr3ea_observedwetweight: cycleRecord.forming.observedWetWeight,
            cr3ea_weightbeforesugarsprinkling: cycleRecord.forming.weightBeforeSugarSprinkling,
            cr3ea_weightaftersugarsprinkling: cycleRecord.forming.weightAfterSugarSprinkling,

            // Baking (zone temperatures 1-7)
            cr3ea_bakingtime: cycleRecord.baking.bakingTime,
            cr3ea_topbakingtempzone1: cycleRecord.baking.topZone1,
            cr3ea_topbakingtempzone2: cycleRecord.baking.topZone2,
            cr3ea_topbakingtempzone3: cycleRecord.baking.topZone3,
            cr3ea_topbakingtempzone4: cycleRecord.baking.topZone4,
            cr3ea_topbakingtempzone5: cycleRecord.baking.topZone5,
            cr3ea_topbakingtempzone6: cycleRecord.baking.topZone6,
            cr3ea_topbakingtempzone7: cycleRecord.baking.topZone7,
            cr3ea_topproducttempafterbaking: cycleRecord.baking.topProductTemp,
            cr3ea_bottombakingtempzone1: cycleRecord.baking.bottomZone1,
            cr3ea_bottombakingtempzone2: cycleRecord.baking.bottomZone2,
            cr3ea_bottombakingtempzone3: cycleRecord.baking.bottomZone3,
            cr3ea_bottombakingtempzone4: cycleRecord.baking.bottomZone4,
            cr3ea_bottombakingtempzone5: cycleRecord.baking.bottomZone5,
            cr3ea_bottombakingtempzone6: cycleRecord.baking.bottomZone6,
            cr3ea_bottombakingtempzone7: cycleRecord.baking.bottomZone7,
            cr3ea_bottomproducttempafterbaking: cycleRecord.baking.bottomProductTemp,
            cr3ea_bakingprofileasperTemplate: cycleRecord.baking.asPerTemplate,
            cr3ea_bakingprofiletaste: cycleRecord.baking.taste,

            // Biscuit Standards, Colour, Finishing, Moisture
            cr3ea_biscuitlength: cycleRecord.standards.biscuitLength,
            cr3ea_biscuitwidth: cycleRecord.standards.biscuitWidth,
            cr3ea_biscuitdiameter: cycleRecord.standards.biscuitDiameter,
            cr3ea_standardssamplecount: cycleRecord.standards.standardsSampleCount,
            cr3ea_biscuitstdweight: cycleRecord.standards.biscuitStdWeight,
            cr3ea_biscuitobservedweight: cycleRecord.standards.biscuitObservedWeight,
            cr3ea_topcolourstandard: cycleRecord.standards.topColourStd,
            cr3ea_topcolourobserved: cycleRecord.standards.topColourObs,
            cr3ea_bottomcolourstandard: cycleRecord.standards.bottomColourStd,
            cr3ea_bottomcolourobserved: cycleRecord.standards.bottomColourObs,
            cr3ea_weightafteroilspray: cycleRecord.standards.weightAfterOilSpray,
            cr3ea_moisturestandard: cycleRecord.standards.moistureStd,
            cr3ea_moistureobserved: cycleRecord.standards.moistureObs
        };

        // If tourId is a valid GUID, bind lookup
        const isValidGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cleanTourId);
        if (isValidGuid) {
            widePayload["cr3ea_qualitytourid@odata.bind"] = `/cr3ea_prod_rajpura_quality_tours(${cleanTourId})`;
        }

        // 2. Always persist to IndexedDB first
        await putToStore(STORES.MIXING_BAKING_CYCLES, {
            id: cycleId,
            tourId: cleanTourId,
            cycle: cycleRecord.cycle,
            data: recordToSave,
            widePayload,
            timestamp: Date.now()
        });

        // 3. Online Dataverse POST
        if (navigator.onLine) {
            let actualToken = token;
            if (!actualToken) {
                const tokenRes = await getAccessToken();
                actualToken = tokenRes?.token;
            }

            if (actualToken) {
                const apiVersion = "9.2";
                const baseApiUrl = ENVIRONMENT_URL || '';
                const tableOptions = [
                    TABLES.MIXING_BAKING
                ];

                for (const tableName of tableOptions) {
                    try {
                        const postUrl = `${baseApiUrl}/api/data/v${apiVersion}/${tableName}`;
                        const res = await fetch(postUrl, {
                            method: "POST",
                            headers: {
                                "Accept": "application/json",
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${actualToken}`,
                                "Prefer": "return=representation"
                            },
                            body: JSON.stringify(widePayload)
                        });

                        if (res.ok) {
                            const resData = await res.json();
                            const serverCycleId = resData.cr3ea_prod_rajpura_mixingandbakingid || cycleId;
                            return {
                                success: true,
                                id: serverCycleId,
                                message: `Cycle ${cycleRecord.cycle} saved successfully to Dataverse`
                            };
                        }
                    } catch (e) {
                        console.warn(`Dataverse post to table '${tableName}' failed:`, e);
                    }
                }
            }
        }

        // 4. Offline / Enqueue Sync
        await enqueueSync({
            id: `sync_mb_cycle_${cycleId}`,
            type: 'CYCLE_SAVE_MIXING_BAKING',
            tourId: cleanTourId,
            payload: widePayload,
            cycleRecord: recordToSave,
            timestamp: Date.now()
        });

        return {
            success: true,
            id: cycleId,
            message: `Cycle ${cycleRecord.cycle} stored locally offline. Will sync when connected.`
        };
    },

    /**
     * Retrieve all cycles for a tour (Dataverse + IndexedDB merge)
     */
    getCyclesForTour: async function (tourId: string, token?: string): Promise<MBCycleRecord[]> {
        const cleanTourId = tourId.replace(/[{}]/g, "").trim().toLowerCase();
        let records: MBCycleRecord[] = [];

        // 1. Fetch from Dataverse if online
        if (navigator.onLine) {
            let actualToken = token;
            if (!actualToken) {
                const tokenRes = await getAccessToken();
                actualToken = tokenRes?.token;
            }

            if (actualToken) {
                const apiVersion = "9.2";
                const baseApiUrl = ENVIRONMENT_URL || '';
                const tableOptions = [TABLES.MIXING_BAKING];

                for (const tableName of tableOptions) {
                    try {
                        const filter = `?$filter=_cr3ea_qualitytourid_value eq ${cleanTourId}&$orderby=createdon asc`;
                        const url = `${baseApiUrl}/api/data/v${apiVersion}/${tableName}${filter}`;

                        const res = await fetch(url, {
                            headers: { "Accept": "application/json", "Authorization": `Bearer ${actualToken}` }
                        });

                        if (res.ok) {
                            const data = await res.json();
                            const rows = data.value || [];

                            if (rows.length > 0) {
                                records = rows.map((r: any) => mapDataverseRowToCycleRecord(r, cleanTourId));
                                break;
                            }
                        }
                    } catch (e) {
                        console.warn(`Could not fetch cycles from table '${tableName}':`, e);
                    }
                }
            }
        }

        // 2. Read from local IndexedDB
        try {
            const localList = await getMixingBakingCyclesForTour(cleanTourId);
            if (localList && localList.length > 0) {
                localList.forEach((item: any) => {
                    const cycleData: MBCycleRecord = item.data || item;
                    const exists = records.some(r => r.cycle === cycleData.cycle);
                    if (!exists) {
                        records.push(cycleData);
                    }
                });
            }
        } catch (e) {
            console.warn("Could not read local Mixing & Baking cycles from IndexedDB:", e);
        }

        records.sort((a, b) => {
            const numA = parseInt(a.cycle.replace(/\D/g, ""), 10) || 0;
            const numB = parseInt(b.cycle.replace(/\D/g, ""), 10) || 0;
            return numA - numB;
        });

        return records;
    },

    /**
     * Query all Mixing & Baking tours for Rajpura Plant
     */
    getTourHistory: async function (token?: string): Promise<any[]> {
        const list: any[] = [];

        if (navigator.onLine) {
            let actualToken = token;
            if (!actualToken) {
                const tokenRes = await getAccessToken();
                actualToken = tokenRes?.token;
            }

            if (actualToken) {
                const apiVersion = "9.2";
                const baseApiUrl = ENVIRONMENT_URL || '';
                try {
                    const filter = `?$filter=(cr3ea_plantid eq '14' or cr3ea_plantid eq '2' or cr3ea_plantid eq 'Rajpura') and (contains(cr3ea_title, 'MixingBaking_') or contains(cr3ea_title, 'Mixing_'))&$orderby=cr3ea_tourstartdate desc&$top=100`;
                    const url = `${baseApiUrl}/api/data/v${apiVersion}/${RAJPURA_TABLES.QUALITY_TOUR}${filter}`;

                    const res = await fetch(url, {
                        headers: { "Accept": "application/json", "Authorization": `Bearer ${actualToken}` }
                    });

                    if (res.ok) {
                        const data = await res.json();
                        (data.value || []).forEach((t: any) => list.push(t));
                    }
                } catch (e) {
                    console.warn("Could not fetch Mixing & Baking tour history from Dataverse:", e);
                }
            }
        }

        // Merge from IndexedDB sessions
        try {
            const localSessions = await getAllFromStore(STORES.MIXING_BAKING_SESSIONS);
            if (localSessions && localSessions.length > 0) {
                localSessions.forEach((ls: any) => {
                    const id = ls.id || ls.localId;
                    const exists = list.some(t => (t.cr3ea_prod_qualitytourid || t.cr3ea_prod_rajpura_quality_tourid) === id);
                    if (!exists) {
                        list.push(ls);
                    }
                });
            }
        } catch (_) {}

        return list;
    },

    /**
     * Upload photo / document to SharePoint tagged with QualityId
     */
    uploadAttachment: async function (tourId: string, cycle: string, file: File, accessToken?: string): Promise<CycleAttachment> {
        const uniqueId = `att_${cycle}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        const previewUrl = URL.createObjectURL(file);

        const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });

        const attItem: CycleAttachment = {
            id: uniqueId,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            previewUrl,
            base64Data,
            timestamp: Date.now()
        };

        if (navigator.onLine && accessToken) {
            try {
                const { SP_SITE_URL, buildSharePointContextInfoUrl, SHAREPOINT_LIBRARIES } = await import('../../../../constants/sharepoint');
                const libraryName = SHAREPOINT_LIBRARIES.MIXING_BAKING || "Quality-Rajpura-MixingBaking";

                const digestRes = await fetch(buildSharePointContextInfoUrl(), {
                    method: "POST",
                    headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                });

                if (digestRes.ok) {
                    const digestData = await digestRes.json();
                    const formDigest = digestData.d.GetContextWebInformation.FormDigestValue;

                    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${libraryName}')/Files/add(url='${encodeURIComponent(file.name)}',overwrite=true)?$expand=ListItemAllFields`;
                    const uploadRes = await fetch(uploadUrl, {
                        method: "POST",
                        headers: {
                            "Accept": "application/json;odata=verbose",
                            "X-RequestDigest": formDigest,
                            "Authorization": `Bearer ${accessToken}`
                        },
                        body: file
                    });

                    if (uploadRes.ok) {
                        const uploadResult = await uploadRes.json();
                        const listItemId = uploadResult.d.ListItemAllFields.Id;
                        attItem.serverRelativeUrl = uploadResult.d.ServerRelativeUrl;

                        // Update metadata QualityId
                        const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${libraryName}')/items(${listItemId})`;
                        await fetch(itemUrl, {
                            method: "POST",
                            headers: {
                                "Accept": "application/json;odata=verbose",
                                "Content-Type": "application/json;odata=verbose",
                                "X-RequestDigest": formDigest,
                                "Authorization": `Bearer ${accessToken}`,
                                "IF-MATCH": "*",
                                "X-HTTP-Method": "MERGE"
                            },
                            body: JSON.stringify({ QualityId: tourId })
                        });
                    }
                }
            } catch (e) {
                console.warn("SharePoint direct upload failed, storing offline attachment:", e);
            }
        }

        return attItem;
    }
};

/**
 * Helper to map Dataverse wide row to MBCycleRecord
 */
function mapDataverseRowToCycleRecord(r: any, tourId: string): MBCycleRecord {
    const cycleRecord = createDefaultCycleRecord(r.cr3ea_cycle || "Cycle-1");

    cycleRecord.id = r.cr3ea_prod_rajpura_mixingandbakingid || r.id;
    cycleRecord.tourId = tourId;
    cycleRecord.cycle = r.cr3ea_cycle || "Cycle-1";
    cycleRecord.cycleTime = r.createdon || new Date().toISOString();
    cycleRecord.batchNo = r.cr3ea_batchno || "";
    cycleRecord.productName = r.cr3ea_productname || "";
    cycleRecord.executiveName = r.cr3ea_observedby || "";
    cycleRecord.shift = r.cr3ea_shift || "Shift 1";
    cycleRecord.lineNo = r.cr3ea_lineno || "Line-1";
    cycleRecord.observedBy = r.cr3ea_observedby || "";
    cycleRecord.productionIncharge = r.cr3ea_productionincharge || "";

    // Ingredients
    cycleRecord.rpo = {
        standard: r.cr3ea_rpostandard || "",
        observed: r.cr3ea_rpoobserved || "",
        remarks: r.cr3ea_rporemarks || "",
        actionTaken: r.cr3ea_rpoactiontaken || ""
    };
    cycleRecord.solidFat = {
        standard: r.cr3ea_solidfatstandard || "",
        observed: r.cr3ea_solidfatobserved || "",
        remarks: r.cr3ea_solidfatremarks || "",
        actionTaken: r.cr3ea_solidfatactiontaken || ""
    };
    cycleRecord.butter = {
        standard: r.cr3ea_butterstandard || "",
        observed: r.cr3ea_butterobserved || "",
        remarks: r.cr3ea_butterremarks || "",
        actionTaken: r.cr3ea_butteractiontaken || ""
    };
    cycleRecord.blackjackInitial = {
        standard: r.cr3ea_blackjackstandard || "",
        observed: r.cr3ea_blackjackobserved || "",
        remarks: r.cr3ea_blackjackremarks || "",
        actionTaken: r.cr3ea_blackjackactiontaken || ""
    };
    cycleRecord.sponge = {
        standard: r.cr3ea_spongetempstandard || "",
        observed: r.cr3ea_spongetempobserved || "",
        remarks: r.cr3ea_spongetempremarks || "",
        actionTaken: r.cr3ea_spongetempactiontaken || ""
    };
    cycleRecord.slurry = {
        standard: r.cr3ea_slurrystandard || "",
        observed: r.cr3ea_slurryobserved || "",
        remarks: r.cr3ea_slurryremarks || "",
        actionTaken: r.cr3ea_slurryactiontaken || ""
    };
    cycleRecord.groundSugarTemp = {
        standard: r.cr3ea_groundsugartempstandard || "",
        observed: r.cr3ea_groundsugartempobserved || "",
        remarks: r.cr3ea_groundsugartempremarks || "",
        actionTaken: r.cr3ea_groundsugartempactiontaken || ""
    };
    cycleRecord.groundSugarParticleSize = {
        standard: r.cr3ea_groundsugarparticlesizestandard || "",
        observed: r.cr3ea_groundsugarparticlesizeobserved || "",
        remarks: r.cr3ea_groundsugarparticlesizeremarks || "",
        actionTaken: r.cr3ea_groundsugarparticlesizeactiontaken || ""
    };

    // Raw Materials
    cycleRecord.rawMaterials = {
        chocoChipsSupplier: r.cr3ea_chocochipssupplier || "",
        chocoChipsTemp: r.cr3ea_chocochipstemp || "",
        chocoChipsCountPerKg: r.cr3ea_chocochipscountperkg || "",
        chocoChipsMfgDate: r.cr3ea_chocochipsmfgdate || "",
        chocoChipsCompoundOrPure: r.cr3ea_chocochipscompoundorpure || "",
        cashewSupplier: r.cr3ea_cashewsupplier || "",
        cashewTemp: r.cr3ea_cashewtemp || "",
        cashewCountPerKg: r.cr3ea_cashewcountperkg || "",
        cashewMfgDate: r.cr3ea_cashewmfgdate || "",
        cashewCompoundOrPure: r.cr3ea_cashewcompoundorpure || "",
        invertSyrupTemp: r.cr3ea_invertsyruptemp || "",
        invertSyrupPh: r.cr3ea_invertsyrupph || "",
        invertSyrupBrix: r.cr3ea_invertsyrupbrix || "",
        blackjack2Temp: r.cr3ea_blackjack2temp || "",
        blackjack2Ph: r.cr3ea_blackjack2ph || "",
        blackjack2Brix: r.cr3ea_blackjack2brix || "",
        flourSupplier: r.cr3ea_floursupplier || ""
    };

    // Minor Ingredients
    cycleRecord.minorIngredients = {
        cocoaPowder: r.cr3ea_cocoapowderdone || "Done",
        smp: r.cr3ea_smpdone || "Done",
        salt1: r.cr3ea_salt1done || "Done",
        abc: r.cr3ea_abcdone || "Done",
        sbc: r.cr3ea_sbcdone || "Done",
        salt2: r.cr3ea_salt2done || "Done",
        others: r.cr3ea_othersdone || "Done"
    };

    // Mixing Dough
    cycleRecord.mixingDough = {
        creamingTimeStd: r.cr3ea_creamingtimestandard || "",
        creamingTimeObs: r.cr3ea_creamingtimeobserved || "",
        mixingTimeStd: r.cr3ea_mixingtimestandard || "",
        mixingTimeObs: r.cr3ea_mixingtimeobserved || "",
        doughTempStd: r.cr3ea_doughtempstandard || "",
        doughTempObs: r.cr3ea_doughtempobserved || "",
        jacketTempStd: r.cr3ea_jackettempstandard || "",
        jacketTempObs: r.cr3ea_jackettempobserved || "",
        doughConsistencyStd: r.cr3ea_doughconsistencystandard || "",
        doughConsistencyObs: r.cr3ea_doughconsistencyobserved || "",
        doughStandingTimeStd: r.cr3ea_doughstandingtimestandard || "",
        doughStandingTimeObs: r.cr3ea_doughstandingtimeobserved || ""
    };

    // Mixing Sponge
    cycleRecord.mixingSponge = {
        spongeProductName: r.cr3ea_spongeproductname || "",
        spongeWaterQuantity: r.cr3ea_spongewaterquantity || "",
        spongeYeastQuantity: r.cr3ea_spongeyeastquantity || "",
        spongeWaterTemp: r.cr3ea_spongewatertemp || "",
        spongeMixingTime: r.cr3ea_spongemixingtime || "",
        fermentationStartTemp: r.cr3ea_fermentationstarttemp || "",
        fermentationRoomTemp: r.cr3ea_fermentationroomtemp || "",
        finalTempAfterFermentation: r.cr3ea_finaltempafterfermentation || "",
        finalPhAfterFermentation: r.cr3ea_finalphafterfermentation || ""
    };

    // Forming
    cycleRecord.forming = {
        typeofMixer: r.cr3ea_typeofmixer || "Vertical",
        moulderRpmStrokes: r.cr3ea_moulderrpmstrokes || "",
        formingSampleCount: r.cr3ea_formingsamplecount || "10",
        standardWetWeight: r.cr3ea_standardwetweight || "",
        observedWetWeight: r.cr3ea_observedwetweight || "",
        weightBeforeSugarSprinkling: r.cr3ea_weightbeforesugarsprinkling || "",
        weightAfterSugarSprinkling: r.cr3ea_weightaftersugarsprinkling || ""
    };

    // Baking
    cycleRecord.baking = {
        bakingTime: r.cr3ea_bakingtime || "",
        topZone1: r.cr3ea_topbakingtempzone1 || "",
        topZone2: r.cr3ea_topbakingtempzone2 || "",
        topZone3: r.cr3ea_topbakingtempzone3 || "",
        topZone4: r.cr3ea_topbakingtempzone4 || "",
        topZone5: r.cr3ea_topbakingtempzone5 || "",
        topZone6: r.cr3ea_topbakingtempzone6 || "",
        topZone7: r.cr3ea_topbakingtempzone7 || "",
        topProductTemp: r.cr3ea_topproducttempafterbaking || "",
        bottomZone1: r.cr3ea_bottombakingtempzone1 || "",
        bottomZone2: r.cr3ea_bottombakingtempzone2 || "",
        bottomZone3: r.cr3ea_bottombakingtempzone3 || "",
        bottomZone4: r.cr3ea_bottombakingtempzone4 || "",
        bottomZone5: r.cr3ea_bottombakingtempzone5 || "",
        bottomZone6: r.cr3ea_bottombakingtempzone6 || "",
        bottomZone7: r.cr3ea_bottombakingtempzone7 || "",
        bottomProductTemp: r.cr3ea_bottomproducttempafterbaking || "",
        asPerTemplate: r.cr3ea_bakingprofileasperTemplate || "Yes",
        taste: r.cr3ea_bakingprofiletaste || ""
    };

    // Standards
    cycleRecord.standards = {
        biscuitLength: r.cr3ea_biscuitlength || "",
        biscuitWidth: r.cr3ea_biscuitwidth || "",
        biscuitDiameter: r.cr3ea_biscuitdiameter || "",
        standardsSampleCount: r.cr3ea_standardssamplecount || "10",
        biscuitStdWeight: r.cr3ea_biscuitstdweight || "",
        biscuitObservedWeight: r.cr3ea_biscuitobservedweight || "",
        topColourStd: r.cr3ea_topcolourstandard || "Golden Brown",
        topColourObs: r.cr3ea_topcolourobserved || "Golden Brown",
        bottomColourStd: r.cr3ea_bottomcolourstandard || "Even Brown",
        bottomColourObs: r.cr3ea_bottomcolourobserved || "Even Brown",
        weightAfterOilSpray: r.cr3ea_weightafteroilspray || "",
        moistureStd: r.cr3ea_moisturestandard || "2.5",
        moistureObs: r.cr3ea_moistureobserved || ""
    };

    return cycleRecord;
}
